import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Button } from './Button';

/**
 * Ochrona przed przypadkowym zamknięciem okna z wpisanymi danymi (prośba Sebastiana 9.10.2026: „jak klikam obok
 * popupa, zamyka okno i usuwa dane”). Bez zmian w formularzach: okno uznaje się za edytowane po pierwszym zdarzeniu
 * `input` / `change` w jego treści (pola tekstowe, listy, pola wyboru). Gdy okno jest edytowane, klik poza oknem
 * i Esc go nie zamykają — pokazuje się pytanie „Zamknąć bez zapisu?”. Krzyżyk, „Anuluj” i zamknięcie po zapisie
 * (wywołanie `onOpenChange(false)` przez formularz) działają bez pytania — to świadome akcje.
 */
export function useDirtyGuard(open: boolean, onOpenChange: (open: boolean) => void) {
  const dirty = useRef(false);
  // listy (Radix Select) wysyłają „change” także przy ustawieniu wartości domyślnej po otwarciu — taka zmiana liczy
  // się dopiero po interakcji osoby z oknem (klik albo klawisz); wpisany tekst („input”) liczy się zawsze
  const interacted = useRef(false);
  const [asking, setAsking] = useState(false);
  const backRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      dirty.current = false;
      interacted.current = false;
      setAsking(false);
    }
  }, [open]);

  useEffect(() => {
    if (asking) backRef.current?.focus();
  }, [asking]);

  const contentProps = {
    onPointerDownCapture: () => {
      interacted.current = true;
    },
    onKeyDownCapture: () => {
      interacted.current = true;
    },
    onInputCapture: () => {
      dirty.current = true;
    },
    onChangeCapture: () => {
      if (interacted.current) dirty.current = true;
    },
    onPointerDownOutside: (event: Event) => {
      if (!dirty.current) return;
      event.preventDefault();
      setAsking(true);
    },
    onFocusOutside: (event: Event) => {
      if (dirty.current) event.preventDefault();
    },
    onEscapeKeyDown: (event: KeyboardEvent) => {
      if (!dirty.current) return;
      event.preventDefault();
      setAsking(true);
    },
  };

  const confirm: ReactNode = asking ? (
    <div
      role="alertdialog"
      aria-label="Niezapisane zmiany"
      className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-elevated px-4 py-3"
    >
      <p className="text-body-s text-fg">Masz niezapisane zmiany. Zamknąć bez zapisu?</p>
      <div className="flex gap-2">
        <Button ref={backRef} variant="ghost" size="s" onClick={() => setAsking(false)}>
          Wróć do formularza
        </Button>
        <Button
          variant="secondary"
          size="s"
          onClick={() => {
            setAsking(false);
            onOpenChange(false);
          }}
        >
          Zamknij bez zapisu
        </Button>
      </div>
    </div>
  ) : null;

  return { contentProps, confirm };
}
